import { makeAutoObservable } from "mobx";
import { Observable } from "rxjs";
import { v1 as uuidv1 } from "uuid";

import { IRootStore } from "@/app/model/rootStore";
import { ServiceTextKeys } from "@/shared/lib";
import {
  IMessage,
  MessageSenderType,
  MessageStatus,
  MessageType,
} from "@/shared/types";
import { getCurrentISODate } from "@/shared/utils";

import { messageSendingService } from "../api/messageSendingService";
import { IMessageChunk, ISendMessageParams, MessageChunkType } from "../types";
import { formatResponseChunk } from "../utils/format";

interface IFailedMessageParams extends ISendMessageParams {
  localId: string;
}

export class MessageSendingStore {
  rootStore: IRootStore;

  public messageText = "";

  private abortController = new AbortController();
  private textDecoder = new TextDecoder("utf-8");

  public isLoading = false;
  public isProcessing = false;

  public failedMessageParams: IFailedMessageParams | null = null;

  constructor(rootStore: IRootStore) {
    makeAutoObservable(this);

    this.rootStore = rootStore;
  }

  clearState() {
    this.setFailedMessageParams(null);
  }

  setMessageText(text: string) {
    this.messageText = text;
  }

  private setIsProcessing(isProcessing: boolean) {
    this.isProcessing = isProcessing;
  }

  private setIsLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  public setFailedMessageParams(params: IFailedMessageParams | null) {
    this.failedMessageParams = params;
  }

  public stopSendMessage() {
    this.abortController.abort();
  }

  private createResponseStream$(
    streamReader: ReadableStreamDefaultReader<Uint8Array>
  ) {
    return new Observable<IMessageChunk>((subscriber) => {
      (async () => {
        try {
          while (true) {
            const { done, value } = await streamReader.read();

            if (done) break;

            const rawChunk = this.textDecoder.decode(value, {
              stream: true,
            });

            const messageChunks = rawChunk
              .split("\n")
              .filter((line) => Boolean(line))
              .map((chunk) => formatResponseChunk(chunk));

            messageChunks.map((chunk) => {
              if (chunk.status === MessageStatus.FAILED) {
                subscriber.error(MessageStatus.FAILED);
              } else {
                subscriber.next(chunk);
              }
            });
          }

          subscriber.complete();
        } catch (error: any) {
          if (error.name === "AbortError") {
            subscriber.error(MessageStatus.STOPPED);
          } else {
            subscriber.error(error.message);
          }
        } finally {
          this.setIsProcessing(false);
        }
      })();
    });
  }

  public async sendStreamMessage(
    params: Omit<ISendMessageParams, "correlation_id">
  ) {
    const user = this.rootStore.userStore.user;

    const { ai_chat_id, msg, type, prompt_id, dialog_id } = params;

    if (!ai_chat_id || !msg || !user) {
      return;
    }

    this.abortController = new AbortController();

    let streamReader: ReadableStreamDefaultReader<Uint8Array> | undefined;

    // stores user local message id
    const localMessageId = crypto.randomUUID();

    // user message
    const requestMessage = {
      ai_chat_id,
      message_id: localMessageId,
      created_at: getCurrentISODate(),
      message_type: type,
      msg,
      status: MessageStatus.IN_PROCESS,
      sender: {
        id: user.user_id,
        type: MessageSenderType.USER,
      },
    };

    // assistant message
    const responseMessage: IMessage = {
      ai_chat_id,
      message_id: crypto.randomUUID(),
      created_at: getCurrentISODate(),
      message_type: MessageType.DATA,
      msg: "",
      status: MessageStatus.IN_PROCESS,
      sender: {
        id: ai_chat_id,
        type: MessageSenderType.AI_CHAT,
      },
    };

    // payload needed if message fails
    const requestMessageParams: ISendMessageParams = {
      ai_chat_id,
      msg,
      type,
      prompt_id,
      dialog_id,
      correlation_id: uuidv1(),
    };

    this.rootStore.messageHistoryStore.addLocalMessage(requestMessage);

    this.setIsLoading(true);
    this.setIsProcessing(true);

    try {
      streamReader = await messageSendingService.sendMessage(
        requestMessageParams,
        this.abortController.signal
      );

      // updates success user message request
      this.rootStore.messageHistoryStore.updateMessageStatus(
        localMessageId,
        MessageStatus.FINISHED
      );

      // adds assistant response local message
      this.rootStore.messageHistoryStore.addLocalMessage(responseMessage);
    } catch (error) {
      // updates failed user message request
      this.rootStore.messageHistoryStore.updateMessageStatus(
        localMessageId,
        MessageStatus.FAILED
      );
      // and stores it
      this.setFailedMessageParams({
        ...requestMessageParams,
        localId: localMessageId,
      });

      this.setIsProcessing(false);

      throw error;
    } finally {
      this.setIsLoading(false);
    }

    if (!streamReader) {
      return;
    }

    const stream = this.createResponseStream$(streamReader);

    stream.subscribe({
      next: (chunk) => {
        // even if response fails, first chunk will always be, having type start
        if (chunk.type === MessageChunkType.START) {
          // since at this time last message always will be assistant response local message, we update its id to match server
          this.rootStore.messageHistoryStore.updateLastMessageId(
            chunk.message_id
          );
          responseMessage.message_id = chunk.message_id;

          this.rootStore.dialogsStore.activeDialogId ??= chunk.dialog_id;
        }

        this.rootStore.messageHistoryStore.updateMessageText(
          chunk.message_id,
          chunk.content
        );
      },
      error: (err) => {
        switch (err) {
          case MessageStatus.FAILED:
            this.rootStore.messageHistoryStore.updateMessageStatus(
              responseMessage.message_id,
              MessageStatus.FAILED
            );
            this.setFailedMessageParams({
              ...requestMessageParams,
              localId: localMessageId,
            });
            break;
          case MessageStatus.STOPPED:
            this.rootStore.messageHistoryStore.updateMessageStatus(
              responseMessage.message_id,
              MessageStatus.STOPPED
            );
            // Adds local service message
            this.rootStore.messageHistoryStore.addLocalMessage({
              ai_chat_id,
              message_id: crypto.randomUUID(),
              created_at: getCurrentISODate(),
              message_type: MessageType.SERVICE,
              msg: "",
              status: MessageStatus.FINISHED,
              service_message_key:
                ServiceTextKeys[
                  ServiceTextKeys.SERVICE_MESSAGE_REQUEST_STOPPED
                ],
              sender: {
                id: ai_chat_id,
                type: MessageSenderType.AI_CHAT,
              },
            });
            break;
          default:
            console.log(err);
        }
      },
      complete: () => {
        this.rootStore.messageHistoryStore.updateMessageStatus(
          responseMessage.message_id,
          MessageStatus.FINISHED
        );
        if (!this.rootStore.uiStore.isMobile) {
          setTimeout(() => this.rootStore.uiStore.focusMessageTextarea(), 0);
        }
      },
    });
  }
}
